import PrepaidCardApi from '@apis/prepaid/PrepaidCardApi';
import userPrepaidCardQueries from '@apis/queries/userPrepaidCardQueries';
import AppModal from '@components/AppModal';
import Text from '@components/Text';
import useCurrency from '@hooks/item/useCurrency';
import { ErrorResponse } from '@models/error';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pushEcommerceEvent } from '@utils/gtm';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface Params {
    orderNo: string;
    amount?: number;
}

declare module '@stackflow/config' {
    interface Register {
        CancelChargeModal: Params;
    }
}

const CancelChargeModal: ActivityComponentType<'CancelChargeModal'> = ({ params }) => {
    const { orderNo, amount } = params;
    const { t } = useTranslation('prepaidCard');
    const { pop } = useFlow();
    const queryClient = useQueryClient();
    const currency = useCurrency();
    const { mutate, isPending } = useMutation({
        mutationFn: () => PrepaidCardApi.cancelPrepaidCardOrder(orderNo),
        async onSuccess() {
            pushEcommerceEvent('refund', {
                transaction_id: orderNo,
                purchase_type: 'PREPAID_CARD_CHARGE',
                value: amount,
                currency,
            });
            pop();
            await queryClient.invalidateQueries({ queryKey: userPrepaidCardQueries._def });
            toast.success(t('log.cancelCharge.success'));
        },
        onError(e: AxiosError<ErrorResponse>) {
            toast.error(e.response?.data.message || t('log.cancelCharge.error'));
        },
    });

    return (
        <AppModal title={t('log.cancelCharge.title')}
            deny={t('log.cancelCharge.close')}
            confirm={t('log.cancelCharge.confirm')}
            onConfirm={() => mutate()}
            confirmDisabled={isPending}
            denyDisabled={isPending}
            disableOutside={isPending} >
            <Text size='title' align='center' multiline>
                {t('log.cancelCharge.description')}
            </Text>
        </AppModal>
    );
};

export default CancelChargeModal;
